/** Team Members — statuses, countries, auth helpers, and sample data (mock until API). */

import {
  buildAccessSummaryLabel,
  buildSamplePermissions,
  getActiveAssignablePermissions as getActiveAssignablePermissionsFromConfig,
  getAppliedSnapshot,
  getPermissionById as getPermissionByIdFromConfig,
  getPermissionPresetForPosition,
  isPermissionActive as isPermissionActiveFromConfig,
  isPermissionOperationallyActive,
  JOB_TITLE_OPTIONS,
  moduleHasAnyPermission,
  normalizeModulePermissions,
} from './permissionsConfig';

export { JOB_TITLE_OPTIONS };

export const OWNER_EMAIL = 'saim.sahi@tradesposter.com';
export const CO_OWNER_EMAIL = 'hamza.zahoor@tradesposter.com';

export const SYSTEM_PROTECTED_ACCESS_LABEL = 'System-Protected Access';
export const OWNER_ACCESS_LABEL = SYSTEM_PROTECTED_ACCESS_LABEL;
export const CO_OWNER_ACCESS_LABEL = SYSTEM_PROTECTED_ACCESS_LABEL;
export const NOT_ASSIGNED = 'Not Assigned';
export const NOT_APPLICABLE = 'Not Applicable';

/** @deprecated Prefer OWNER_ACCESS_LABEL — kept for Permissions & Access bridges. */
export const OWNER_PERMISSION_LABEL = OWNER_ACCESS_LABEL;
/** @deprecated Prefer CO_OWNER_ACCESS_LABEL — kept for Permissions & Access bridges. */
export const CO_OWNER_PERMISSION_LABEL = CO_OWNER_ACCESS_LABEL;

export const TEAM_MEMBER_STATUS = {
  PENDING_ACCESS: 'Pending Access',
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
  /** @deprecated Use INACTIVE */
  DEACTIVATED: 'Inactive',
  /** @deprecated Password reset no longer changes Status */
  PASSWORD_RESET_REQUIRED: 'Active',
};

export const PLATFORM_ROLE = {
  OWNER: 'Platform Owner',
  CO_OWNER: 'Co-owner',
  TEAM_MEMBER: 'Team Member',
};

export const APPROVED_WORK_COUNTRIES = [
  { value: 'Australia', label: 'Australia', dialCode: '+61' },
  { value: 'Pakistan', label: 'Pakistan', dialCode: '+92' },
];

export const OFFICIAL_EMAIL_DOMAIN = 'tradesposter.com';

export const TEAM_MEMBER_ADD_SUCCESS =
  'Team Member saved. Access can now be added through Permissions & Access.';

/** Active Standard Permissions from Configuration — Permissions & Access */
export const TEAM_PERMISSIONS = buildSamplePermissions()
  .filter((p) => isPermissionOperationallyActive(p))
  .map((p) => {
    const applied = getAppliedSnapshot(p) || p;
    return {
      id: p.id,
      name: applied.permissionName,
      status: 'Active',
      jobTitles: applied.jobTitles,
    };
  });

export const PASSWORD_RULE_MESSAGE =
  'Password must contain at least 8 characters, one uppercase letter, one lowercase letter, one number and one special character.';

export const isValidTeamPassword = (value) => {
  const v = (value || '').trim();
  if (v.length < 8) return false;
  return /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/.test(v);
};

export const generateTemporaryPassword = () => {
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const lower = 'abcdefghijkmnopqrstuvwxyz';
  const digits = '23456789';
  const special = '@$!%*?&';
  const pick = (set) => set[Math.floor(Math.random() * set.length)];
  const base = [pick(upper), pick(lower), pick(digits), pick(special)];
  const all = upper + lower + digits + special;
  while (base.length < 12) base.push(pick(all));
  for (let i = base.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [base[i], base[j]] = [base[j], base[i]];
  }
  return base.join('');
};

export const getPermissionById = (id) => {
  const permission = getPermissionByIdFromConfig(id, buildSamplePermissions());
  if (!permission) return TEAM_PERMISSIONS.find((p) => p.id === id);
  const applied = getAppliedSnapshot(permission) || permission;
  const status = applied.status || permission.status;
  return {
    id: permission.id,
    name: applied.permissionName || permission.permissionName,
    status: status ? status.charAt(0).toUpperCase() + status.slice(1) : '—',
    jobTitles: applied.jobTitles || permission.jobTitles,
  };
};

export const getActiveAssignablePermissions = (jobTitle) => {
  const permissions = getActiveAssignablePermissionsFromConfig(jobTitle, buildSamplePermissions());
  return permissions.map((p) => ({
    id: p.id,
    name: p.permissionName,
    status: 'Active',
    jobTitles: p.jobTitles,
  }));
};

export const isPermissionActive = (permissionId) =>
  isPermissionActiveFromConfig(permissionId, buildSamplePermissions());

export const nextTeamMemberId = (existingMembers) => {
  const nums = (existingMembers || [])
    .map((m) => {
      const match = /^TM-(\d+)$/i.exec(m.teamMemberId || '');
      return match ? parseInt(match[1], 10) : 0;
    })
    .filter((n) => n > 0);
  const next = nums.length ? Math.max(...nums) + 1 : 1;
  return `TM-${String(next).padStart(4, '0')}`;
};

const resolveActorEmail = (auth) =>
  (
    auth?.user?.tradesPosterEmail ||
    auth?.user?.personalEmail ||
    auth?.personalEmail ||
    ''
  ).toLowerCase();

/** Mock: superadmin session maps to Platform Owner for development. */
export const isTeamOwnerActor = (auth) => {
  if (auth?.roleType === 'superadmin' && auth?.accessToken) return true;
  return resolveActorEmail(auth) === OWNER_EMAIL;
};

export const isTeamCoOwnerActor = (auth) => resolveActorEmail(auth) === CO_OWNER_EMAIL;

export const canViewTeam = (auth, { hasTeamMembersViewGrant = false } = {}) =>
  isTeamOwnerActor(auth) || isTeamCoOwnerActor(auth) || Boolean(hasTeamMembersViewGrant);

export const canViewPermissionsAccess = (auth) => canViewTeam(auth);

export const canManagePermissionsAccess = (auth) => isTeamOwnerActor(auth);

export const getActorDisplayName = (auth) => {
  const first = auth?.user?.firstName ?? auth?.firstName ?? '';
  const last = auth?.user?.lastName ?? auth?.lastName ?? '';
  const name = `${first} ${last}`.trim();
  return name || 'Saim Sahi';
};

export const getActorAccountId = (auth) => {
  const fromUser = auth?.user?.teamMemberId ?? auth?.user?.accountId ?? auth?.teamMemberId;
  if (fromUser) return String(fromUser);
  if (isTeamOwnerActor(auth)) return 'TM-0001';
  if (isTeamCoOwnerActor(auth)) return 'TM-0002';
  return '—';
};

export const getCountryDialCode = (country) => {
  const match = APPROVED_WORK_COUNTRIES.find((c) => c.value === country);
  return match?.dialCode || '';
};

export const isOfficialWorkEmail = (email) => {
  const value = (email || '').trim().toLowerCase();
  return value.endsWith(`@${OFFICIAL_EMAIL_DOMAIN}`);
};

export const mobileMatchesApprovedCountry = (mobileNumber, country) => {
  const dial = getCountryDialCode(country);
  if (!dial) return false;
  const normalized = String(mobileNumber || '').replace(/\s+/g, '');
  return normalized.startsWith(dial);
};

/** Valid stored access that can resume on Activate. */
export const memberHasValidStoredAccess = (member) => {
  if (!member || member.isOwner || member.isCoOwner) return false;
  const team = member.team;
  const position = member.position;
  if (!team || team === NOT_ASSIGNED || team === NOT_APPLICABLE) return false;
  if (!position || position === NOT_ASSIGNED || position === NOT_APPLICABLE) return false;
  const granted = normalizeModulePermissions(member.modulePermissions || []).filter(
    moduleHasAnyPermission,
  );
  return granted.length > 0;
};

/** Build Permissions & Access column / summary label. */
export const buildPermissionsAccessLabel = ({
  isOwner,
  isCoOwner,
  status,
  team,
  position,
  customPermission,
  hasCustomisedAccess,
  defaultAccessLabel,
} = {}) => {
  if (isOwner || isCoOwner) return SYSTEM_PROTECTED_ACCESS_LABEL;
  const pending =
    status === TEAM_MEMBER_STATUS.PENDING_ACCESS ||
    !team ||
    team === NOT_ASSIGNED ||
    !position ||
    position === NOT_ASSIGNED;
  if (pending) return NOT_ASSIGNED;
  return buildAccessSummaryLabel({
    position,
    defaultAccessLabel,
    hasCustomisedAccess: Boolean(hasCustomisedAccess || customPermission),
    customPermission,
  });
};

function withAccessFields(member) {
  if (member.isOwner || member.isCoOwner) {
    return {
      ...member,
      team: NOT_APPLICABLE,
      position: NOT_APPLICABLE,
      jobTitle: NOT_APPLICABLE,
      permissionsAccessLabel: SYSTEM_PROTECTED_ACCESS_LABEL,
      permissionName: SYSTEM_PROTECTED_ACCESS_LABEL,
      defaultAccessLabel: SYSTEM_PROTECTED_ACCESS_LABEL,
    };
  }
  if (
    member.status === TEAM_MEMBER_STATUS.PENDING_ACCESS ||
    !member.position ||
    member.position === NOT_ASSIGNED
  ) {
    return {
      ...member,
      permissionPreset: null,
      hasCustomisedAccess: false,
      permissionsAdded: [],
      permissionsRemoved: [],
      modulePermissions: [],
      dashboard: null,
      permissionsAccessLabel: NOT_ASSIGNED,
      permissionName: NOT_ASSIGNED,
    };
  }

  const preset = getPermissionPresetForPosition(member.position);
  const hasCustomisedAccess = Boolean(member.hasCustomisedAccess || member.customPermission);
  const defaultAccessLabel =
    member.defaultAccessLabel || preset?.defaultAccessLabel || `${member.position} Access`;
  const permissionsAccessLabel = buildAccessSummaryLabel({
    position: member.position,
    defaultAccessLabel,
    hasCustomisedAccess,
    customPermission: member.customPermission,
  });

  return {
    ...member,
    permissionPreset: member.permissionPreset || preset?.presetName || `${member.position} Preset`,
    defaultAccessLabel,
    dashboard: member.dashboard || preset?.dashboard || '',
    hasCustomisedAccess,
    permissionsAdded: member.permissionsAdded || [],
    permissionsRemoved: member.permissionsRemoved || [],
    modulePermissions:
      member.modulePermissions?.length > 0
        ? member.modulePermissions
        : preset?.modulePermissions || [],
    permissionName: defaultAccessLabel,
    permissionsAccessLabel,
  };
}

/** Illustrative sample records aligned with Team Members Screen 1. */
export const buildSampleTeamMembers = () =>
  [
    {
      id: 'tm-1',
      teamMemberId: 'TM-0001',
      name: 'Saim Sahi',
      firstName: 'Saim',
      lastName: 'Sahi',
      workEmail: OWNER_EMAIL,
      mobileNumber: '+61 400 000 001',
      approvedWorkCountry: 'Australia',
      team: NOT_APPLICABLE,
      position: NOT_APPLICABLE,
      jobTitle: NOT_APPLICABLE,
      platformRole: PLATFORM_ROLE.OWNER,
      permissionId: null,
      permissionName: SYSTEM_PROTECTED_ACCESS_LABEL,
      customPermission: null,
      defaultAccessLabel: SYSTEM_PROTECTED_ACCESS_LABEL,
      permissionsAccessLabel: SYSTEM_PROTECTED_ACCESS_LABEL,
      status: TEAM_MEMBER_STATUS.ACTIVE,
      memberSince: '2026-02-01T09:00:00.000Z',
      lastUpdated: '2026-02-01T09:00:00.000Z',
      lastUpdatedBy: 'System',
      isOwner: true,
      isCoOwner: false,
      requirePasswordChange: false,
    },
    {
      id: 'tm-2',
      teamMemberId: 'TM-0002',
      name: 'Hamza Zahoor',
      firstName: 'Hamza',
      lastName: 'Zahoor',
      workEmail: CO_OWNER_EMAIL,
      mobileNumber: '+61 400 000 002',
      approvedWorkCountry: 'Australia',
      team: NOT_APPLICABLE,
      position: NOT_APPLICABLE,
      jobTitle: NOT_APPLICABLE,
      platformRole: PLATFORM_ROLE.CO_OWNER,
      permissionId: null,
      permissionName: SYSTEM_PROTECTED_ACCESS_LABEL,
      customPermission: null,
      defaultAccessLabel: SYSTEM_PROTECTED_ACCESS_LABEL,
      permissionsAccessLabel: SYSTEM_PROTECTED_ACCESS_LABEL,
      status: TEAM_MEMBER_STATUS.ACTIVE,
      memberSince: '2026-02-01T09:05:00.000Z',
      lastUpdated: '2026-02-01T09:05:00.000Z',
      lastUpdatedBy: 'System',
      isOwner: false,
      isCoOwner: true,
      requirePasswordChange: false,
    },
    {
      id: 'tm-3',
      teamMemberId: 'TM-0016',
      name: 'Noah Williams',
      firstName: 'Noah',
      lastName: 'Williams',
      workEmail: 'noah.williams@tradesposter.com',
      mobileNumber: '+61 456 789 012',
      approvedWorkCountry: 'Australia',
      team: 'Support Team',
      position: 'Support',
      jobTitle: 'Support',
      platformRole: PLATFORM_ROLE.TEAM_MEMBER,
      permissionId: 'PER-0001',
      customPermission: null,
      hasCustomisedAccess: false,
      permissionsAdded: [],
      permissionsRemoved: [],
      modulePermissions: [
        {
          module: 'support_tickets',
          view: true,
          add: true,
          edit: true,
          archive: false,
          deactivate: false,
          dataAccessScope: 'assigned_records',
        },
      ],
      defaultAccessLabel: 'Support Tickets',
      permissionsAccessLabel: 'Support Tickets',
      status: TEAM_MEMBER_STATUS.ACTIVE,
      memberSince: '2026-05-10T10:00:00.000Z',
      lastUpdated: '2026-07-28T14:35:00.000Z',
      lastUpdatedBy: 'Saim Sahi',
      isOwner: false,
      isCoOwner: false,
      requirePasswordChange: false,
    },
    {
      id: 'tm-4',
      teamMemberId: 'TM-0017',
      name: 'Aisha Khan',
      firstName: 'Aisha',
      lastName: 'Khan',
      workEmail: 'aisha.khan@tradesposter.com',
      mobileNumber: '+92 300 123 4567',
      approvedWorkCountry: 'Pakistan',
      team: NOT_ASSIGNED,
      position: NOT_ASSIGNED,
      jobTitle: NOT_ASSIGNED,
      platformRole: PLATFORM_ROLE.TEAM_MEMBER,
      permissionId: null,
      permissionName: NOT_ASSIGNED,
      customPermission: null,
      defaultAccessLabel: null,
      permissionsAccessLabel: NOT_ASSIGNED,
      status: TEAM_MEMBER_STATUS.PENDING_ACCESS,
      memberSince: '2026-05-15T11:20:00.000Z',
      lastUpdated: '2026-05-15T11:20:00.000Z',
      lastUpdatedBy: 'Saim Sahi',
      isOwner: false,
      isCoOwner: false,
      requirePasswordChange: false,
    },
    {
      id: 'tm-5',
      teamMemberId: 'TM-0018',
      name: 'Daniel Lee',
      firstName: 'Daniel',
      lastName: 'Lee',
      workEmail: 'daniel.lee@tradesposter.com',
      mobileNumber: '+61 434 567 890',
      approvedWorkCountry: 'Australia',
      team: 'Development Team',
      position: 'Back-end Developer',
      jobTitle: 'Back-end Developer',
      platformRole: PLATFORM_ROLE.TEAM_MEMBER,
      permissionId: 'PER-0002',
      customPermission: null,
      hasCustomisedAccess: true,
      permissionsAdded: [],
      permissionsRemoved: [],
      modulePermissions: [
        {
          module: 'configuration',
          view: true,
          add: false,
          edit: true,
          archive: false,
          deactivate: false,
          dataAccessScope: 'all_area_records',
        },
        {
          module: 'settings',
          view: true,
          add: false,
          edit: false,
          archive: false,
          deactivate: false,
          dataAccessScope: 'all_area_records',
        },
      ],
      defaultAccessLabel: 'Configuration — Validation · Configuration — Notifications',
      permissionsAccessLabel: 'Configuration — Validation · Configuration — Notifications',
      status: TEAM_MEMBER_STATUS.INACTIVE,
      memberSince: '2026-05-17T10:00:00.000Z',
      lastUpdated: '2026-07-27T16:10:00.000Z',
      lastUpdatedBy: 'Saim Sahi',
      isOwner: false,
      isCoOwner: false,
      requirePasswordChange: false,
    },
  ].map(withAccessFields);
